import React, { memo, useCallback } from 'react';
import { Handle, Position } from 'reactflow';
import {
    FiZap,
    FiShield,
    FiTarget,
    FiArrowUp,
    FiArrowDown,
    FiLogOut,
    FiClock,
    FiLayers
} from 'react-icons/fi';
import NodeWrapper from './NodeWrapper';
import { getNodeAccent, isNodeShownOnChart, buildNodeVisualTogglePatch } from '../config/nodeVisualConfig';
import { useNodeVisualOverrides } from '../../../../../hooks/useVisualOverrides';

const ICON_MAP = {
    'enter_long': FiArrowUp,
    'enter_short': FiArrowDown,
    'sl': FiShield,
    'tp': FiTarget,
    'exit_long': FiLogOut,
    'exit_short': FiLogOut,
    'time_exit': FiClock,
    'buy': FiZap,
    'sell': FiZap
};

const SignalNode = memo(({ id, data, selected }) => {
  const { effectiveData, write: writeDisplay } = useNodeVisualOverrides(id, data);

  //! R1-T3 — ONE toggle helper + the ONE display writer; no direct ReactFlow node write for display keys.
  const toggleChart = useCallback((e) => {
      e.stopPropagation();
      writeDisplay?.(buildNodeVisualTogglePatch('signal', effectiveData, 'visibility', { defaultType: 'marker' }));
  }, [writeDisplay, effectiveData]);

  const action = data.action;
  const isBuy = action?.includes('long') || action === 'buy';

  // Action-DIRECTION accent (semantic tokens): long entry/TP → profit; short/SL → loss;
  // time/plain exits → warn. Single source of truth via getNodeAccent.
  const accent = getNodeAccent('signal', data);
  const type = data.type?.toUpperCase() || (isBuy ? 'LONG' : 'SHORT');

  // LONG/SHORT position badge — profit (long) / loss (short) tint.
  let badgeStyle = isBuy
        ? { background: 'var(--badge-profit-bg)', color: 'var(--badge-profit-fg)' }
        : { background: 'var(--badge-loss-bg)', color: 'var(--badge-loss-fg)' };
  let badgeText = type;

  // Custom Badges for Risk/Time
  if (action === 'sl' || action === 'tp') {
      badgeStyle = { background: 'var(--bg-3)', color: 'var(--fg-2)' };
      badgeText = `${type} RISK`;
  } else if (action === 'time_exit') {
      badgeStyle = { background: 'var(--badge-warn-bg)', color: 'var(--badge-warn-fg)' };
      badgeText = `${type} TIME`;
  }

  const isMultiStage = data.execution_strategy === 'multi_stage';
  const stageNum = data.stage || 1;

  const MAX_HANDLES = 6;
  const isSLTP = action === 'sl' || action === 'tp';
  const isExit = isSLTP || action === 'time_exit' || action === 'exit_long' || action === 'exit_short';
  const isCustomMode = isExit && data.params?.exit_mode === 'CUSTOM';
  const showDualHandles = isSLTP ? isCustomMode : isExit;
  const activeInputs = isExit
      ? (data.inputs || []).filter(inp => inp.type === 'link').slice(0, MAX_HANDLES)
      : [];

  const PositionBadge = (
      <div className="px-1.5 py-0.5 rounded text-[8px] font-black tracking-tighter" style={badgeStyle}>
          {badgeText}
      </div>
  );

  const Icon = data.icon || ICON_MAP[data.action] || FiZap;

  // --> Extract scalar value from {value, is_valid} object shape (MTF warmup support)
  const rawValue = data.value;
  const resolvedValue = typeof rawValue === 'object' && rawValue !== null ? rawValue.value : rawValue;

  // Shared handle style (accent-tinted) + the amber trigger/price handle
  const sigHandle = { background: accent };
  const warnHandle = { background: 'var(--warning)' };

  return (
    <NodeWrapper
        compact={true}
        selected={selected}
        type="Signal"
        accent={accent}
        badge={PositionBadge}
        icon={Icon}
        onToggleChart={toggleChart}
        isChartVisible={isNodeShownOnChart('signal', effectiveData)}
        isHighlighted={data.isHighlighted}
    >
        <div className="py-2 px-3 rounded-lg flex items-center justify-between gap-3 text-sm font-bold bg-bg-3">
            <div className="flex items-center gap-2">
                <Icon size={18} className="drop-shadow-sm" style={{ color: accent }} />
                <span className="uppercase tracking-wider text-fg-1">
                    {data.action === 'sl' ? 'Stop Loss' :
                     data.action === 'tp' ? 'Take Profit' :
                     data.action === 'time_exit' ? 'Time Exit' :
                     data.action?.replace('_', ' ')}
                </span>
            </div>

            {/* Live State Indicator */}
            <div
                className="w-2.5 h-2.5 rounded-full transition-all duration-300"
                style={{
                    background: resolvedValue === true ? 'var(--color-profit)'
                        : resolvedValue === false ? 'var(--color-loss)'
                        : 'var(--fg-3)',
                    transform: resolvedValue === true ? 'scale(1.1)' : 'scale(1)',
                }}
            />
        </div>

        <div className="flex flex-row justify-between items-start mt-1.5 w-full">
            {/* Left Column: Input Handles */}
            <div className="flex flex-col gap-1.5 w-full">
                {(() => {
                    if (isExit) {
                        return (
                            <>
                                {activeInputs.length > 1 ? (
                                    activeInputs.map((inp, idx) => (
                                        <div key={inp.id} className="relative flex items-center justify-start pl-1 h-4">
                                            <span className="text-[8px] font-bold tracking-tight" style={{ color: accent }}>◀ SOURCE {idx + 1}</span>
                                            <Handle
                                                type="target"
                                                id={inp.id}
                                                position={Position.Left}
                                                className="!w-2.5 !h-2.5 !border-2 !border-[var(--bg-2)] !left-[-13px] transition-transform hover:scale-125"
                                                style={{ top: '50%', ...sigHandle }}
                                            />
                                        </div>
                                    ))
                                ) : (
                                    <div className="relative flex items-center justify-start pl-1 h-4">
                                        <span className="text-[8px] font-bold tracking-tight" style={{ color: accent }}>◀ SOURCE</span>
                                        <Handle
                                            type="target"
                                            id={isSLTP && activeInputs.length === 1 ? activeInputs[0].id : 'input_0'}
                                            position={Position.Left}
                                            className="!w-2.5 !h-2.5 !border-2 !border-[var(--bg-2)] !left-[-13px] transition-transform hover:scale-125"
                                            style={{ top: '50%', ...sigHandle }}
                                        />
                                    </div>
                                )}

                                {showDualHandles && (
                                    <div className="relative flex items-center justify-start pl-1 h-4 mt-1">
                                        <span className="text-[8px] font-bold tracking-tight" style={{ color: 'var(--warning)' }}>◀ TRIGGER</span>
                                        <Handle
                                            type="target"
                                            id="trigger_input"
                                            position={Position.Left}
                                            className="!w-2.5 !h-2.5 !border-2 !border-[var(--bg-2)] !left-[-13px] transition-transform hover:scale-125"
                                            style={{ top: '50%', ...warnHandle }}
                                        />
                                    </div>
                                )}
                            </>
                        );
                    }

                    // Entry Nodes
                    return (
                        <div className="relative flex items-center justify-start pl-1 h-4">
                            <span className="text-[8px] font-bold tracking-tight" style={{ color: accent }}>◀ SIGNAL</span>
                            <Handle
                                type="target"
                                id="input_0"
                                position={Position.Left}
                                className="!w-2.5 !h-2.5 !border-2 !border-[var(--bg-2)] !left-[-13px] transition-transform hover:scale-125"
                                style={{ top: '50%', ...sigHandle }}
                            />
                        </div>
                    );
                })()}
            </div>

            {/* Right Column: Execution Strategy Badge */}
            <div className="flex justify-end pr-1 mt-0.5 whitespace-nowrap">
                {isSLTP ? (
                    data.params?.exit_mode === 'CUSTOM' ? (
                        <span className="text-[9px] font-bold" style={{ color: 'var(--warning)' }}>CUSTOM</span>
                    ) : activeInputs.length > 1 ? (
                        <div
                            className="flex items-center gap-1 pl-1 pr-1.5 py-0.5 rounded"
                            style={{ background: 'var(--badge-role-bg)', color: 'var(--badge-role-fg)' }}
                            title="Shared — wired to multiple entry stages."
                        >
                            <FiLayers size={10} />
                            <span className="text-[8px] font-black tracking-tighter">SHARED</span>
                        </div>
                    ) : activeInputs.length === 1 && isMultiStage ? (
                        <div
                            className="flex items-center gap-1 pl-1 pr-1.5 py-0.5 rounded"
                            style={{ background: 'var(--badge-info-bg)', color: 'var(--badge-info-fg)' }}
                            title={`Wired to Entry Stage ${stageNum}.`}
                        >
                            <FiLayers size={10} />
                            <span className="text-[8px] font-black tracking-tighter">STAGE {stageNum}</span>
                        </div>
                    ) : activeInputs.length === 1 ? (
                        <span className="text-[9px] font-bold text-fg-3">SINGLE</span>
                    ) : (
                        <span className="text-[9px] font-bold text-fg-3">UNLINKED</span>
                    )
                ) : isMultiStage ? (
                    <div
                        className="flex items-center gap-1 pl-1 pr-1.5 py-0.5 rounded"
                        style={{ background: 'var(--badge-info-bg)', color: 'var(--badge-info-fg)' }}
                    >
                        <FiLayers size={10} />
                        <span className="text-[8px] font-black tracking-tighter">STAGE {stageNum}</span>
                    </div>
                ) : (
                    <span className="text-[9px] font-bold text-fg-3">SINGLE</span>
                )}
            </div>
        </div>

        {/* Output Handles (Entry Nodes Only) */}
        {!isExit && (
            <div className="absolute right-[-8px] top-1/2 -translate-y-1/2 flex flex-col gap-2">
                <Handle
                    type="source"
                    position={Position.Right}
                    id="value"
                    className="!w-2.5 !h-2.5 !border-2 !border-[var(--bg-2)] transition-transform hover:scale-125"
                    style={sigHandle}
                />
                <Handle
                    type="source"
                    position={Position.Right}
                    id="entry_price"
                    className="!w-2 !h-2 !border-2 !border-[var(--bg-2)] transition-transform hover:scale-125"
                    style={warnHandle}
                    title="Entry Price"
                />
            </div>
        )}

    </NodeWrapper>
  );
});

export default SignalNode;
